import CheckIcon from "@mui/icons-material/Check";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import LinkIcon from "@mui/icons-material/Link";
import { type FC, useEffect, useState } from "react";

import { ROUTES } from "~/constants/routing";
import * as Buttons from "~/feature/citeEmbed/citeEmbed.styles";
import { CITE_COPIED_MS, CITE_COPY } from "~/feature/citeEmbed/constants";
import type {
  ICitationLeader,
  ICiteEmbedEvent,
} from "~/feature/citeEmbed/types";
import { getCitationText } from "~/feature/citeEmbed/utils/getCitationText";
import { copyTextToClipboard } from "~/feature/event/components/ActionButtons/utils";
import { getPublicSiteOrigin } from "~/utils/getSiteOrigin";

import * as Styled from "./CiteTab.styles";

type CopiedTarget = "citation" | "link";

interface ICiteTabProps {
  event: ICiteEmbedEvent;
  leader: ICitationLeader | null;
}

export const CiteTab: FC<ICiteTabProps> = ({ event, leader }) => {
  const [copied, setCopied] = useState<{ target: CopiedTarget } | null>(null);
  const citation = leader ? getCitationText(event.title, leader) : null;

  useEffect(() => {
    if (!copied) return;

    const timer = window.setTimeout(() => setCopied(null), CITE_COPIED_MS);

    return () => window.clearTimeout(timer);
  }, [copied]);

  const copy = async (target: CopiedTarget, text: string) => {
    if (await copyTextToClipboard(text)) setCopied({ target });
  };

  const handleCopyLink = () =>
    copy(
      "link",
      `${getPublicSiteOrigin()}${ROUTES.event.createURL(event.slug)}`
    );

  const isCopied = (target: CopiedTarget) => copied?.target === target;

  return (
    <Styled.Root>
      {citation && <Styled.Citation>{citation}</Styled.Citation>}
      <Styled.Actions>
        {citation && (
          <Buttons.InkButton
            type="button"
            onClick={() => copy("citation", citation)}
          >
            {isCopied("citation") ? <CheckIcon /> : <ContentCopyOutlinedIcon />}
            {isCopied("citation") ? CITE_COPY.copied : CITE_COPY.citation}
          </Buttons.InkButton>
        )}
        <Buttons.OutlinedButton type="button" onClick={handleCopyLink}>
          {isCopied("link") ? <CheckIcon /> : <LinkIcon />}
          {isCopied("link") ? CITE_COPY.copied : CITE_COPY.link}
        </Buttons.OutlinedButton>
      </Styled.Actions>
    </Styled.Root>
  );
};
